import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { friendlyMessageForCode } from '../shared/utils/api-error.util';

function bestErrorMessage(error: HttpErrorResponse): string | null {
    const payload = error?.error as any;
    const nested =
        (payload && typeof payload === 'object' && (payload.error?.message || payload.message)) ||
        null;

    if (typeof nested === 'string' && nested.trim()) return nested.trim();
    return null;
}

function errorCode(error: HttpErrorResponse): string | null {
    const payload = error?.error as any;
    if (payload && typeof payload === 'object') {
        return String(payload.error?.code || payload.code || '').trim() || null;
    }
    return null;
}

function sanitizeHttpFailureMessage(message: string): string {
    const m = String(message || '').match(/^Http failure response for .*?:\s*(.*)$/i);
    if (!m) return message;
    const tail = (m[1] || '').trim();
    if (!tail) return 'Request failed. Please try again.';
    if (/^0\s+/i.test(tail) || /unknown error/i.test(tail)) {
        return 'Network error. Please try again.';
    }
    return `Request failed: ${tail}`;
}

export const httpErrorInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    return next(req).pipe(
        catchError((err) => {
            if (!(err instanceof HttpErrorResponse)) {
                return throwError(() => err);
            }

            const backendMessage = bestErrorMessage(err);
            const code = errorCode(err);
            // Prefer the friendly code-map text; fall back to the backend message.
            const friendlyFromCode = code ? friendlyMessageForCode(code) : null;
            const userMessage =
                err.status === 0
                    ? 'Network error. Please try again.'
                    : friendlyFromCode || backendMessage || sanitizeHttpFailureMessage(err.message || '');

            try {
                const current = String((err as any).message || '');
                if (!current || current !== userMessage) {
                    (err as any).rawMessage = current || undefined;
                    (err as any).message = userMessage;
                }
                (err as any).userMessage = userMessage;
                if (code) (err as any).errorCode = code;
            } catch {
                // If the HttpErrorResponse is non-writable for some reason, fall through.
            }

            return throwError(() => err);
        })
    );
};

